import React, { useCallback, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation, Trans } from 'next-i18next';
import { useMutation } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { API, APIRoutes } from '@utils/api';
import { toast } from 'react-toastify';

import TextHeader, { SIZE, WEIGHT, TAG } from '@components/TextHeader';
import TextInput, { INPUT_TYPES as type } from '@components/Form/Input';
import Control, { CONTROL_TYPE } from '@components/Form/Control';
import Button, { BUTTON_TYPE } from '@components/Button';
import Preloader from '@components/Preloader';
import Link from '@components/Link';
import { InputsContainer, Form, CaptchaContainer } from '@components/ContactForm/styles';
import { ErrorMessage } from '@styles/globalStyles';

interface ContactFormProps {
  onSuccessCallback: () => void;
}

type ContactForm = {
  message: ContactFormPayload;
  recaptchaToken: string;
};

type ContactFormPayload = {
  topic: string;
  email: string;
  body: string;
  newsletterAgreement: string;
  termsOfServiceAgreement: string;
  recaptchaToken: string;
};

function Contact({ onSuccessCallback }: ContactFormProps) {
  const { t } = useTranslation(['contact', 'register']);
  const recaptchaRef = useRef(null);
  const contactRef = useRef(null);

  const validationSchema = Yup.object().shape({
    topic: Yup.string().required(t('Topic required')).min(4, t('Topic must be at least 4 characters long')),
    email: Yup.string().email(t('Invalid Email')).required(t('Email required')),
    body: Yup.string().required(t('Message required')).min(5, t('Message must be at least 5 characters long')),
    termsOfServiceAgreement: Yup.boolean().required(t('RODO Required')).oneOf([true], t('RODO Required')),
    recaptchaToken: Yup.string().required(t('reCAPTCHA is required')),
  });

  const formOptions = { resolver: yupResolver(validationSchema) };
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm(formOptions);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errors]);

  const { mutate: sendContactForm, isLoading } = useMutation((data: ContactForm) => API.post(APIRoutes.contact, data));

  const onSubmit = (formData: ContactFormPayload) => {
    const payload = {
      message: formData,
      recaptchaToken: formData.recaptchaToken,
    };

    delete payload.message.recaptchaToken;

    sendContactForm(payload, {
      onError: ({ response: { data } }) => {
        toast.error(data.errors);
      },
      onSuccess: () => {
        onSuccessCallback();
        reset();
      },
    });
  };

  const recaptchaHandler = useCallback(
    (token: string) => {
      setValue('recaptchaToken', token);
    },
    [setValue]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)} ref={contactRef}>
      {isLoading && <Preloader overlay />}
      <TextHeader size={SIZE.SM} weight={WEIGHT.BOLD} tag={TAG.H1}>
        {t('Contact')}
      </TextHeader>
      <InputsContainer>
        <Controller
          name="topic"
          defaultValue=""
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <TextInput
              name={name}
              inputType={type.TEXT}
              onChange={onChange}
              value={value}
              label={t('topic')}
              error={errors[name]}
              placeholder={t('type in')}
            />
          )}
        />
        <Controller
          name="email"
          defaultValue=""
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <TextInput
              name={name}
              inputType={type.TEXT}
              onChange={onChange}
              value={value}
              label="E-mail"
              error={errors[name]}
              placeholder={t('type in')}
            />
          )}
        />
      </InputsContainer>
      <Controller
        name="body"
        defaultValue=""
        control={control}
        render={({ field: { name, onChange, value } }) => (
          <TextInput
            name={name}
            inputType={type.TEXT}
            onChange={onChange}
            value={value}
            label={t('message')}
            error={errors[name]}
            textArea
            placeholder={t('message')}
          />
        )}
      />
      <Controller
        control={control}
        name="termsOfServiceAgreement"
        render={({ field: { name, onChange, value } }) => (
          <Control
            onChange={onChange}
            value={value}
            checked={value}
            name={name}
            error={errors[name]}
            label={
              <Trans i18nKey="register:personal_data_protection_contact">
                Wyrażam zgodę na przesłanie Newslettera zgodnie z{' '}
                <Link href="/documents/regulamin-uslugi-newsletter">Regulaminem świadczenia usługi newsletter *</Link>
              </Trans>
            }
            type={CONTROL_TYPE.CHECKBOX}
          />
        )}
      />
      <CaptchaContainer>
        <ReCAPTCHA onChange={recaptchaHandler} ref={recaptchaRef} sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY} />
        {errors && errors.recaptchaToken ? <ErrorMessage>{errors.recaptchaToken.message}</ErrorMessage> : null}
      </CaptchaContainer>
      <Button type={BUTTON_TYPE.SUBMIT}>{t('send')}</Button>
    </Form>
  );
}

export default Contact;
